import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../../constants/track-events'
import { useNotificationContext, usePartnerContext } from '../../context'
import { mutation, query } from '../../graphql'
import { trackEvent } from '../../helpers/analytics'
import {
  computeExpiresAt,
  formatAssessmentDeadline,
  getCommonExpiresAfterDays,
  isDeadlineRunning,
  pickerDateToExpiresAt,
  toPickerDate
} from '../../helpers/assessment-deadline'
import { useCreateLog } from '../../hooks/use-create-log'
import Button from '../button'
import Checkbox from '../checkbox'
import Datepicker from '../react-datepicker/datepicker'
import InfoCallout from '../tailwind/InfoCallout'
import Modal from '../tailwind/Modal'

const TRANSLATION_LOCATION = 'components.career.send_reminder_bulk.'

// Geteilter Default statt einer neuen Map bei jedem Render
const NO_CAREERS = new Map()

const SendReminderBulk = ({
  data,
  careers_map = NO_CAREERS,
  updateHandler,
  open,
  setOpen
}) => {
  const { t, i18n } = useTranslation()
  const { success } = useNotificationContext()
  const { partner } = usePartnerContext()

  const { spaces } = data

  const { createSpaceHistoryLog } = useCreateLog()

  const [isLoading, setIsLoading] = useState(false)

  // Laufende Fristen ändert die Erinnerung nie. Das Häkchen setzt nur
  // abgelaufene Fristen neu, ohne Häkchen bekommen diese Talente keine
  // Erinnerung, weil sie den Test nicht mehr starten könnten. Ein Datum für
  // alle setzt die Aktion „Bearbeitungszeit verlängern".
  const runningSpaces = spaces.filter(({ expiresAt }) =>
    isDeadlineRunning(expiresAt)
  )
  const expiredSpaces = spaces.filter(
    ({ expiresAt }) => !isDeadlineRunning(expiresAt)
  )
  const expiredCount = expiredSpaces.length
  const [resetExpired, setResetExpired] = useState(false)
  // Das Datum bekommen nur abgelaufene Fristen, der Vorschlag nimmt deshalb die
  // Bearbeitungszeit ihrer Stellen
  const expiresAfterDays = getCommonExpiresAfterDays(
    partner.app_settings,
    expiredSpaces.map(
      ({ career_id }) => careers_map.get(career_id)?.app_settings
    )
  )
  const defaultDay = useMemo(
    () => toPickerDate(computeExpiresAt(expiresAfterDays)),
    [expiresAfterDays]
  )
  const [pickedDay, setPickedDay] = useState(null)

  // Defaults erst beim Öffnen setzen. Die Komponente ist auf der Talents-Seite
  // dauerhaft gemountet, beim Mount ist noch nichts ausgewählt.
  useEffect(() => {
    if (!open) return
    setResetExpired(expiredCount > 0)
    setPickedDay(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const selectedDay = pickedDay || defaultDay
  const recipients = resetExpired
    ? [...runningSpaces, ...expiredSpaces]
    : runningSpaces
  const skippedCount = spaces.length - recipients.length

  const logAndUpdateExpiresAt = async () => {
    const event = { context: 'SYSTEM', status: 'REMINDED' }

    for (const space of recipients) {
      const { id: space_id, career_id } = space

      await createSpaceHistoryLog({ space_id, career_id, ...event })
    }

    if (!resetExpired) return

    const expiresAt = pickerDateToExpiresAt(selectedDay)
    for (const { id: space_id } of expiredSpaces) {
      await mutation({
        mutation: 'updateSpace',
        input: { id: space_id, expiresAt }
      })
    }
  }

  const remindTalents = async () => {
    setIsLoading(true)

    try {
      await logAndUpdateExpiresAt()

      await Promise.all(
        recipients.map(
          ({ id: space_id, career_id, partner_id, email, language }) => {
            return query({
              query: 'inviteTalent',
              variables: {
                space_id,
                career_id,
                partner_id,
                email,
                language,
                email_template: 'remind'
              }
            })
          }
        )
      )
    } catch (err) {
      setIsLoading(false)
      return
    }

    setIsLoading(false)
    updateHandler()
    trackEvent(trackEvents.REMIND_TALENTS)
    success(
      skippedCount
        ? t(TRANSLATION_LOCATION + 'send_reminder_success_skipped', {
            sent: recipients.length,
            skipped: skippedCount
          })
        : t(TRANSLATION_LOCATION + 'send_reminder_success')
    )
    setOpen(false)
  }

  return (
    <Modal open={open} setOpen={() => setOpen(false)} size='3xl'>
      <div className='mt-3 text-center sm:mt-5'>
        <h2 className='text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl'>
          {t(TRANSLATION_LOCATION + 'send_reminder_modal_title')}
        </h2>
        <p className='mx-auto mt-2 max-w-lg text-sm text-gray-700'>
          {t(TRANSLATION_LOCATION + 'popup_description', {
            number: spaces.length
          })}
        </p>
      </div>
      <div className='mt-8 flex justify-center px-12'>
        <p className='block max-w-lg text-center text-sm text-gray-700'>
          {t(TRANSLATION_LOCATION + 'hint')}
        </p>
      </div>
      <div className='mt-8 px-12'>
        <p className='block text-sm font-medium text-gray-700'>
          {t(TRANSLATION_LOCATION + 'reset_expired_title')}
        </p>
        {expiredCount > 0 && (
          <div className='mt-3 flex items-start'>
            <Checkbox
              id='send-reminder-bulk-reset-expired'
              label={t(TRANSLATION_LOCATION + 'reset_expired_deadlines_label')}
              description={t(
                TRANSLATION_LOCATION + 'reset_expired_deadlines_description',
                { count: expiredCount }
              )}
              checked={resetExpired}
              onChange={(event) => setResetExpired(event.target.checked)}
            />
          </div>
        )}
        {expiredCount > 0 && resetExpired && (
          <div className='ml-7 mt-3'>
            <Datepicker
              selected={selectedDay}
              onChange={(date) => setPickedDay(date)}
              minDate={toPickerDate(new Date().toISOString())}
            />
            <p className='mt-2 text-sm text-gray-500'>
              {t(TRANSLATION_LOCATION + 'expired_deadline_new_hint', {
                count: expiredCount,
                date: formatAssessmentDeadline(
                  pickerDateToExpiresAt(selectedDay),
                  i18n.language
                )
              })}
            </p>
          </div>
        )}
        {expiredCount > 0 && !resetExpired && (
          <InfoCallout className='mt-4'>
            {t(TRANSLATION_LOCATION + 'expired_hint', {
              count: expiredCount
            })}
          </InfoCallout>
        )}
        {runningSpaces.length > 0 && (
          <InfoCallout tone='neutral' className='mt-4'>
            {t(TRANSLATION_LOCATION + 'deadline_unchanged_hint', {
              count: runningSpaces.length
            })}
          </InfoCallout>
        )}
      </div>
      <div className='mt-16 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3 md:flex md:justify-center'>
        <Button
          text={t(TRANSLATION_LOCATION + 'send_reminder_modal_cancel_action')}
          onClick={() => setOpen(false)}
          appearance='w-full-gray'
          className='md:w-80'
        />
        <Button
          text={t(TRANSLATION_LOCATION + 'send_reminder_modal_submit_action')}
          onClick={remindTalents}
          appearance='w-full-base'
          className='md:w-80'
          isLoading={isLoading}
          disabled={!recipients.length}
        />
      </div>
    </Modal>
  )
}

SendReminderBulk.propTypes = {
  data: PropTypes.object.isRequired,
  // Stellen nach ID wie auf der Talents-Seite, für die Bearbeitungszeit
  careers_map: PropTypes.instanceOf(Map),
  updateHandler: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

export default SendReminderBulk
